/**
 * Parc Fermé — Fast Simon header search integration (file name kept for its existing include; loads on every page).
 *
 * 1. SKIN. The Fast Simon "no-code" header autocomplete renders its overlay inside an OPEN shadow root
 *    (light-DOM hosts: div.fast-ac-container inside the search form, .mobile-fast-ac-container on <body>).
 *    Plain <link>/<style> tags in the light DOM can't cross that boundary, so a single <style> is injected
 *    into each host's shadowRoot. It never touches Fast Simon's handlers, search logic or DOM structure.
 *
 * FAST SIMON SDK AUDIT FIXES 2026-10-25 (Lion Shpoliansky, Fast Simon CSM — "CMC X FS SDK Audit"):
 *
 * 2. HAND-OFF (audit #1, high). The Fast Simon dropdown attaches to Horizon's header search box (#cmdk-input),
 *    which also runs Shopify predictive search. Both used to run on every keystroke, and Shopify's results +
 *    "View all" button sat underneath the Fast Simon panel. Now:
 *      - (revised same day, "Fast Simon for all search") Shopify's /search/suggest is never called while Fast
 *        Simon works; it only answers if Fast Simon's panel hasn't appeared 2.5s after typing stops (safety net);
 *      - while the Fast Simon panel is open
 *        Shopify's results area ("View all" included) is hidden and its arrow-key / Enter handling stands down
 *        so it can't act on hidden results. Escape still closes and clears as before;
 *      - a shopper who typed before Fast Simon was ready gets Fast Simon results for what's already typed.
 * 3. WARM-UP (audit #1). Fast Simon loads its stylesheet and settings lazily on the first keystroke, which is
 *    most of the delay before its dropdown answers. On the first sign of search intent (pointer over / focus on
 *    the header search button, the search box, or Cmd+K) its hosts are preconnected, the settings response is
 *    pre-fetched (cacheable 48s, same URL Fast Simon requests) and its stylesheet is preloaded.
 * 4. PERFORMANCE (audit #4). The old version watched every DOM change on the whole page forever and rescanned
 *    the document each time. Now: one scan at start-up and on load; when the search box gets focus (Fast Simon
 *    builds its hosts on focus) a short-lived observer runs until the hosts are styled (max 8s); the only
 *    lasting observers are scoped to Fast Simon's own shadow roots, to track its panel's visibility.
 * 5. CART VISITED (audit #5). Each time the cart opens (Rebuy Smart Cart, or the /cart page), Fast Simon's
 *    "cartVisited" report is sent: through
 *    window.PFFastSimon (assets/pf-fastsimon.js) where that file runs, otherwise by loading the Fast Simon SDK on
 *    demand (only on a drawer open, never on page load).
 *
 * Everything is wrapped so a failure here can never break the page: worst case, the header search behaves as it
 * did before (Shopify predictive search + Fast Simon side by side).
 */
(function () {
  try {
    if (window.__pfFsHeader) return;
    window.__pfFsHeader = true;

    var STYLE_ID = 'pf-fastsimon-shadow-style';
    var HOST_SEL = 'div.fast-ac-container, .mobile-fast-ac-container';
    var INPUT_SEL = '#cmdk-input';
    var FS_STORE_ID = 14510982;
    var FS_STORE_UUID = '71cfa029-7a65-481a-ba61-2bac5634fd84';
    var FS_SDK_URL = 'https://assets.fastsimon.com/sdk/latest/fast_simon_sdk.js';

    var CSS = [
      /* Panel shell */
      '[id^="fast-autocomplete"], .autocomplete-inner {',
      '  background: #131318 !important;',
      '  color: #f5f5f3 !important;',
      '}',
      '[id^="fast-autocomplete"] {',
      '  border: 1px solid #26262e !important;',
      '  box-shadow: 0 12px 32px rgba(0,0,0,0.55) !important;',
      '}',
      '.autocomplete-inner {',
      '  scrollbar-width: thin;',
      '  scrollbar-color: #26262e #131318;',
      '}',
      '.autocomplete-inner::-webkit-scrollbar { width: 8px; height: 8px; }',
      '.autocomplete-inner::-webkit-scrollbar-track { background: #131318; }',
      '.autocomplete-inner::-webkit-scrollbar-thumb { background: #26262e; border-radius: 4px; }',
      '.autocomplete-inner::-webkit-scrollbar-thumb:hover { background: #86868f; }',

      /* Section headings: Recent Searches / Popular Searches / Collections */
      '.fs-autocomplete-header, .header.fs-autocomplete-header {',
      '  font-family: "IBM Plex Mono", monospace !important;',
      '  font-size: 12px !important;',
      '  line-height: 1.4 !important;',
      '  text-transform: uppercase !important;',
      '  letter-spacing: 0.14em !important;',
      '  color: #86868f !important;',
      '  background: transparent !important;',
      '}',
      '.fs-autocomplete-header svg, .fs-autocomplete-header path {',
      '  fill: #86868f !important;',
      '  stroke: #86868f !important;',
      '}',

      /* Recent / popular search terms rendered as bordered mono pill chips */
      '.fast-ac-item.line-item {',
      '  display: inline-flex !important;',
      '  align-items: center !important;',
      '  border: 1px solid #26262e !important;',
      '  border-radius: 999px !important;',
      '  background: #08080a !important;',
      '  color: #f5f5f3 !important;',
      '  font-family: "IBM Plex Mono", monospace !important;',
      '  font-size: 12px !important;',
      '  letter-spacing: 0.04em !important;',
      '  padding: 6px 14px !important;',
      '  margin: 4px !important;',
      '  transition: border-color 0.15s ease, color 0.15s ease, transform 0.15s ease !important;',
      '}',
      '.fast-ac-item.line-item a, .fast-ac-item.line-item span {',
      '  color: inherit !important;',
      '  font-size: inherit !important;',
      '  font-family: inherit !important;',
      '}',
      '.fast-ac-item.line-item:hover, .fast-ac-item.line-item:focus-within {',
      '  border-color: #e10600 !important;',
      '  color: #e10600 !important;',
      '  transform: translateY(-2px) !important;',
      '}',

      /* Collections list links */
      '.fs-category-list a {',
      '  color: #f5f5f3 !important;',
      '  font-family: "IBM Plex Mono", monospace !important;',
      '  font-size: 12.5px !important;',
      '  text-decoration: none !important;',
      '  transition: color 0.15s ease !important;',
      '}',
      '.fs-category-list a:hover, .fs-category-list a:focus-visible {',
      '  color: #e10600 !important;',
      '}',

      /* Product cards in the horizontal result rail */
      '.fast-ac-item.fs-product {',
      '  background: transparent !important;',
      '  border-radius: 4px !important;',
      '  transition: transform 0.18s ease, border-color 0.18s ease !important;',
      '}',
      '.fast-ac-item.fs-product:hover, .fast-ac-item.fs-product:focus-visible {',
      '  transform: translateY(-3px) !important;',
      '}',
      '.fast-ac-item.fs-product:focus-visible {',
      '  outline: 2px solid #ffd100 !important;',
      '  outline-offset: 2px !important;',
      '}',

      /* Product image wrapper — leave the <img> itself untouched */
      '.fs-product-image {',
      '  background: #101014 !important;',
      '  border: 1px solid #26262e !important;',
      '  border-radius: 4px !important;',
      '  transition: border-color 0.18s ease !important;',
      '}',
      '.fast-ac-item.fs-product:hover .fs-product-image {',
      '  border-color: #86868f !important;',
      '}',

      /* Product title / vendor */
      '.fs-product-title, .fs-product-title .label {',
      '  color: #f5f5f3 !important;',
      '  font-size: 12.5px !important;',
      '  line-height: 1.4 !important;',
      '  font-family: "IBM Plex Mono", monospace !important;',
      '}',
      '.fs-product-vendor, .fs-product-vendor .label {',
      '  color: #9b9ba4 !important;',
      '  font-size: 12px !important;',
      '  font-family: "IBM Plex Mono", monospace !important;',
      '  text-transform: uppercase !important;',
      '  letter-spacing: 0.08em !important;',
      '}',

      /* Prices: mono white default, gold sale price + grey strikethrough compare-at */
      '.fs-product-price-wrapper {',
      '  font-family: "IBM Plex Mono", monospace !important;',
      '  font-size: 12.5px !important;',
      '}',
      '.fs-price.actual-price {',
      '  color: #f5f5f3 !important;',
      '}',
      '.fs-product-price-wrapper.compareExist .fs-price.actual-price {',
      '  color: #ffd100 !important;',
      '  font-weight: 600 !important;',
      '}',
      '.fs-price.compare {',
      '  color: #86868f !important;',
      '  text-decoration: line-through !important;',
      '}',

      /* "Show all results for …" footer (2026-10-25): Fast Simon paints it #f8f8f8, which left our near-white text
         unreadable. With Shopify's "View all" now hidden under the panel, this is the way to the full results. */
      '.summery {',
      '  background: #08080a !important;',
      '  color: #f5f5f3 !important;',
      '  border-top: 1px solid #26262e !important;',
      '  font-family: "IBM Plex Mono", monospace !important;',
      '  font-size: 12px !important;',
      '  letter-spacing: 0.1em !important;',
      '  text-transform: uppercase !important;',
      '  cursor: pointer !important;',
      '  padding: 12px 32px !important;',
      '  transition: color 0.15s ease !important;',
      '}',
      '.summery a, .summery span, .summery b, .summery strong { color: inherit !important; }',
      '.summery:hover, .summery:focus-within { color: #e10600 !important; }',

      /* Defensive: any stray input rendered inside the shadow root */
      '[id^="fast-autocomplete"] input {',
      '  background: #0e0e12 !important;',
      '  color: #f5f5f3 !important;',
      '  border: 1px solid #26262e !important;',
      '  font-size: 12.5px !important;',
      '}',
      '[id^="fast-autocomplete"] input:focus-visible {',
      '  outline: 2px solid #ffd100 !important;',
      '  outline-offset: 1px !important;',
      '}',

      /* Generic focus-visible safety net for anything else in the panel */
      '[id^="fast-autocomplete"] a:focus-visible, [id^="fast-autocomplete"] button:focus-visible {',
      '  outline: 2px solid #ffd100 !important;',
      '  outline-offset: 2px !important;',
      '}'
    ].join('\n');

    /* Light-DOM rule for the hand-off: while the Fast Simon panel is open, Shopify's results area under it
       (product/query lists and the "View all" button) is hidden. The input row itself is untouched. */
    var LIGHT_CSS =
      'predictive-search-component.pf-fs-ac-active .predictive-search-form__content-wrapper { display: none !important; }';

    function addLightStyle() {
      if (document.getElementById('pf-fs-header-style')) return;
      var s = document.createElement('style');
      s.id = 'pf-fs-header-style';
      s.textContent = LIGHT_CSS;
      (document.head || document.documentElement).appendChild(s);
    }

    /* ---------- 1. Skin ---------- */

    function injectInto(shadowRoot) {
      if (!shadowRoot || shadowRoot.getElementById(STYLE_ID)) return false;
      var style = document.createElement('style');
      style.id = STYLE_ID;
      style.setAttribute('data-pf-injected', '1');
      style.textContent = CSS;
      shadowRoot.appendChild(style);
      return true;
    }

    function tryHost(host) {
      if (!host) return;
      var sr = host.shadowRoot;
      if (!sr) return;
      // Checks the style itself (not just a flag), so a Fast Simon re-render that wipes it gets re-skinned.
      if (injectInto(sr)) host.setAttribute('data-pf-shadow-skinned', '1');
      watchPanel(host, sr);
    }

    /* Returns how many hosts exist and are fully set up. */
    function scan() {
      var hosts = document.querySelectorAll(HOST_SEL);
      var ready = 0;
      for (var i = 0; i < hosts.length; i++) {
        tryHost(hosts[i]);
        if (hosts[i].shadowRoot && hosts[i].shadowRoot.getElementById(STYLE_ID)) ready++;
      }
      return { found: hosts.length, ready: ready };
    }

    /* ---------- 4. Short-lived host watch (replaces the old permanent page-wide observer) ---------- */

    var hostWatch = null;
    function watchForHosts() {
      var r = scan();
      if (r.found >= 2 && r.ready === r.found) { onHostsReady(); return; }  // desktop + mobile hosts both styled
      if (hostWatch) return;
      var queued = false;
      var mo = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          var s = scan();
          if (s.found >= 2 && s.ready === s.found) stop();
        });
      });
      function stop() {
        if (!hostWatch) return;
        mo.disconnect();
        clearTimeout(hostWatch.timer);
        hostWatch = null;
        onHostsReady();
      }
      mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
      hostWatch = { timer: setTimeout(function () { scan(); stop(); }, 8000) };
    }

    /* ---------- 2. Hand-off between Shopify predictive search and Fast Simon ---------- */

    var fsHasRendered = false;
    var fsDown = false;   // set only by the header-search safety net below

    function searchComponent() {
      var input = document.querySelector(INPUT_SEL);
      return input ? input.closest('predictive-search-component') : null;
    }

    function panelOpen() {
      var hosts = document.querySelectorAll(HOST_SEL);
      for (var i = 0; i < hosts.length; i++) {
        var sr = hosts[i].shadowRoot;
        if (!sr) continue;
        var panel = sr.querySelector('[id^="fast-autocomplete"]');
        if (panel && panel.offsetHeight > 0 && getComputedStyle(panel).display !== 'none' &&
            panel.querySelector('.fast-ac-item, .fs-autocomplete-header')) return true;
      }
      return false;
    }

    function patchComponent(comp) {
      if (!comp || comp.__pfFsPatched) return;
      if (typeof comp.search !== 'function' || typeof comp.onSearchKeyDown !== 'function') return;
      comp.__pfFsPatched = true;

      // Horizon resolves on:input="/search" and on:keydown="/onSearchKeyDown" on the instance at event time,
      // so wrapping the instance properties is enough; Horizon's own file is untouched.
      /* 2026-10-25 (Rudy: "Fast Simon must be used for all search"): Fast Simon owns the header search from the
         first keystroke; Shopify's /search/suggest is never called while Fast Simon is working. Safety net only: if
         Fast Simon's panel hasn't appeared 2.5s after the shopper stops typing (Fast Simon down or blocked),
         Shopify's search answers that query, and hands back as soon as Fast Simon's panel shows again. */
      var origSearch = comp.search;
      var watchdog = null;
      comp.search = function () {
        if (fsDown) return origSearch.apply(this, arguments);
        var self = this, args = arguments;
        clearTimeout(watchdog);
        watchdog = setTimeout(function () {
          var input = document.querySelector(INPUT_SEL);
          if (!input || document.activeElement !== input || !input.value.trim() || panelOpen()) return;
          fsDown = true;
          origSearch.apply(self, args);
        }, 2500);
      };

      var origKey = comp.onSearchKeyDown;
      comp.onSearchKeyDown = function (event) {
        // While the Fast Simon panel is open, its own keyboard handling applies; Shopify's would act on hidden
        // results. Escape still goes to Horizon (close + clear). With the panel closed, nothing changes.
        if (event && event.key !== 'Escape' && comp.classList.contains('pf-fs-ac-active')) return;
        return origKey.apply(this, arguments);
      };
    }

    function syncState() {
      var comp = searchComponent();
      if (!comp) return;
      patchComponent(comp);
      var open = panelOpen();
      if (open) { fsHasRendered = true; fsDown = false; }
      comp.classList.toggle('pf-fs-ac-active', open);
    }

    function watchPanel(host, sr) {
      if (host.__pfFsWatch) return;
      host.__pfFsWatch = true;
      var queued = false;
      var mo = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          if (!sr.getElementById(STYLE_ID)) injectInto(sr);
          syncState();
        });
      });
      // Scoped to Fast Simon's own panel: only changes while the dropdown is in use.
      mo.observe(sr, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      mo.observe(host, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    }

    /* A shopper who typed before Fast Simon attached: replay the current text to it once its hosts exist.
       Horizon ignores these synthetic events (its search() needs a real inputType), so no Shopify request. */
    var replayed = false;
    function onHostsReady() {
      syncState();
      if (replayed) return;
      var input = document.querySelector(INPUT_SEL);
      if (!input || document.activeElement !== input || !input.value.trim()) return;
      replayed = true;
      setTimeout(function () {
        try {
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true, key: 'Unidentified' }));
        } catch (e) {}
      }, 50);
    }

    /* ---------- 3. Warm-up on search intent ---------- */

    var warmedNet = false;
    var warmedCss = false;
    function warm() {
      if (!warmedNet) {
        warmedNet = true;
        ['https://suggest.fastsimon.com', 'https://api.fastsimon.com', 'https://settings.fastsimon.com'].forEach(function (h) {
          var l = document.createElement('link');
          l.rel = 'preconnect';
          l.href = h;
          l.crossOrigin = 'anonymous';
          document.head.appendChild(l);
        });
        try {
          // Fast Simon reads "autocomplete" on the live theme and "autocomplete_unpublished" on theme previews.
          var live = !!(window.Shopify && window.Shopify.theme && window.Shopify.theme.role === 'main');
          fetch('https://settings.fastsimon.com/settings/' + (live ? 'autocomplete' : 'autocomplete_unpublished') +
            '?uuid=' + FS_STORE_UUID + '&store_id=' + FS_STORE_ID, { mode: 'cors', credentials: 'omit' }).catch(function () {});
        } catch (e) {}
      }
      if (!warmedCss) {
        // The bundle is requested as <link rel=preload> or <script>; its ?v= pins the matching stylesheet + strings.
        var el = document.querySelector('link[href*="fast-simon-autocomplete.umd.js"], script[src*="fast-simon-autocomplete.umd.js"]');
        var src = el && (el.href || el.src);
        if (!src && window.performance && performance.getEntriesByType) {
          // The init script may load the bundle without leaving an element behind; the resource timing entry has the URL.
          var entries = performance.getEntriesByType('resource');
          for (var i = 0; i < entries.length; i++) {
            if (/fast-simon-autocomplete\.umd\.js\?v=/.test(entries[i].name)) { src = entries[i].name; break; }
          }
        }
        if (src) {
          warmedCss = true;
          var css = document.createElement('link');
          css.rel = 'preload';
          css.as = 'style';
          css.href = src.replace('fast-simon-autocomplete.umd.js', 'fast-simon-autocomplete.css');
          document.head.appendChild(css);
          var ver = (src.match(/[?&]v=(\d+\.\d+\.\d+)/) || [])[1];
          if (ver) {
            var strings = document.createElement('link');
            strings.rel = 'preload';
            strings.as = 'fetch';
            strings.crossOrigin = 'anonymous';
            strings.href = 'https://static-autocomplete.fastsimon.com/locals/en.json?v=' + ver;
            document.head.appendChild(strings);
          }
        }
      }
    }

    function isSearchTrigger(el) {
      if (!el || !el.closest) return false;
      return !!el.closest(INPUT_SEL + ', #search-modal, [on\\:click*="search-modal"], .search-action, [aria-controls="search-modal"]');
    }

    function onIntent(e) {
      if (isSearchTrigger(e.target)) warm();
    }

    document.addEventListener('pointerover', onIntent, { passive: true });
    document.addEventListener('touchstart', onIntent, { passive: true });
    document.addEventListener('keydown', function (e) {
      if (e.metaKey && e.key === 'k') warm();
    });

    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches(INPUT_SEL)) {
        warm();
        patchComponent(searchComponent());
        watchForHosts();   // Fast Simon builds its hosts on focus; re-checks if it ever rebuilds them
      }
    });

    /* ---------- 5. Cart visited ---------- */

    var sdkWaiters = null;
    function withSdk(cb) {
      var sdk = window.FastSimonSDK;
      if (sdk && typeof sdk.event === 'function') { cb(); return; }
      if (sdkWaiters) { sdkWaiters.push(cb); return; }
      sdkWaiters = [cb];
      function flush() {
        var list = sdkWaiters || [];
        sdkWaiters = [];
        list.forEach(function (fn) { try { fn(); } catch (e) {} });
      }
      if (window.__pfFastSimonLoading) {
        // Something else on the page is already loading the SDK: wait for it (max ~10s).
        var n = 0;
        var iv = setInterval(function () {
          if (window.FastSimonSDK && typeof window.FastSimonSDK.event === 'function') { clearInterval(iv); flush(); }
          else if (++n > 40) clearInterval(iv);
        }, 250);
        return;
      }
      window.__pfFastSimonLoading = true;
      window.__fast_simon_sdk_config = {
        store_uuid: FS_STORE_UUID,
        store_id: FS_STORE_ID,
        type: 'MPA',
        withAttributes: true,
        onReady: flush
      };
      var script = document.createElement('script');
      script.src = FS_SDK_URL;
      script.async = true;
      script.onerror = function () { sdkWaiters = null; window.__pfFastSimonLoading = false; };
      document.head.appendChild(script);
    }

    function reportCartVisited() {
      if (window.PFFastSimon && typeof window.PFFastSimon.reportCartVisited === 'function') {
        window.PFFastSimon.reportCartVisited();
        return;
      }
      withSdk(function () {
        var names = window.FastSimonEventName || (window.FastSimonSDK && window.FastSimonSDK.FastEventName) || {};
        try { window.FastSimonSDK.event({ eventName: names.CartVisited || 'cartVisited', data: {} }); } catch (e) {}
      });
    }

    /* The cart on this store is Rebuy Smart Cart (Horizon's own drawer is hidden in pf-polish), which announces
       each opening with "rebuy:smartcart.show". Horizon's drawer event is kept as a fallback in case the cart is
       ever switched back, and a visit to the /cart page counts once. Repeated show events within 1.5s (Rebuy
       can fire more than one per opening) are treated as a single opening. */
    var lastCartReport = 0;
    function cartOpened() {
      var now = Date.now();
      if (now - lastCartReport < 1500) return;
      lastCartReport = now;
      reportCartVisited();
    }
    document.addEventListener('rebuy:smartcart.show', cartOpened);
    document.addEventListener('theme-drawer:open', function (e) {
      var drawer = e.target;
      if (!drawer || !drawer.querySelector) return;
      if (!drawer.querySelector('cart-drawer-component') && !(drawer.closest && drawer.closest('cart-drawer-component'))) return;
      cartOpened();
    });
    if (/^\/cart\/?$/.test(location.pathname)) {
      window.addEventListener('load', cartOpened, { once: true });
    }

    /* A native <dialog> closed with Escape skips Horizon's close handling; re-check the panel state then. */
    document.addEventListener('close', function () { requestAnimationFrame(syncState); }, true);

    /* ---------- Start-up ---------- */

    addLightStyle();
    scan();
    window.addEventListener('load', function () {
      scan();
      /* Audit #5: the SDK runs in multi-page mode, so reports such as cartVisited are queued in localStorage
         ("post-load-event") and sent by the SDK on the next page where it starts (verified 2026-10-25: a cart
         open on the homepage was sent as post_load …&cart=1 on the next collection page). Collection, search and
         product pages already start it (assets/pf-fastsimon.js); on other pages it is started here in idle
         time, and only when something is actually waiting to be sent. */
      var pending = false;
      try { pending = !!localStorage.getItem('post-load-event'); } catch (e) {}
      if (!pending || window.FastSimonSDK || window.__pfFastSimonLoading) return;
      (window.requestIdleCallback || function (f) { setTimeout(f, 1500); })(function () { withSdk(function () {}); });
    }, { once: true });
  } catch (e) {
    // Never throw — this file only enhances the header search and reporting.
  }
})();
